import type { ApiClient } from './client';

export interface PrivatePhoto {
  upload_id: string;
  url: string;
}

export interface SelfProfile {
  photos: PrivatePhoto[];
  pick: string | number;
  src: 'photos' | 'avatar' | null;
  ready: boolean;
  name: string;
  variants_count: number | null;
  version: number;
}

export interface Person {
  id: number;
  name: string;
  rel: string;
  age: string | null;
  gender: string | null;
  is_pet: boolean;
  kind: string;
  photos: PrivatePhoto[];
  selectable: boolean;
  disabled_reason: string | null;
  version: number;
  created_at: string;
}

export interface PersonChanges {
  name?: string;
  rel?: string;
  age?: string | null;
  gender?: string | null;
  photo_upload_ids?: string[];
}

/** Отправка версии обязательна и для первого изменения пустого SelfProfile (версия 0). */
export const people = (client: ApiClient) => ({
  avatars: (beforeId?: number) => client.request<{ items: UserAvatar[]; next_before_id: number | null }>('/app/avatars', { query: { before_id: beforeId, limit: 20 } }),
  avatarPhoto: (id: number, signal?: AbortSignal) => client.download(`/app/avatars/${id}/image`, signal),
  updateAvatar: (id: number, changes: { variant_name?: string; voice_id?: number | null }, version: number) =>
    client.request<UserAvatar>(`/app/avatars/${id}`, { method: 'PATCH', body: changes, ifMatch: `"${version}"` }),
  removeAvatar: (id: number) => client.request<void>(`/app/avatars/${id}`, { method: 'DELETE' }),
  self: () => client.request<SelfProfile>('/app/people/me'),
  updateSelf: (changes: { photo_upload_ids?: string[]; pick?: 'photos' | number }, version: number) =>
    client.request<SelfProfile>('/app/people/me', {
      method: 'PUT', body: changes, ifMatch: `"${version}"`,
    }),
  list: () => client.request<{ items: Person[] }>('/app/people'),
  create: (body: PersonChanges, idempotencyKey: string) =>
    client.request<Person>('/app/people', { method: 'POST', body, idempotency: idempotencyKey }),
  update: (id: number, changes: PersonChanges, version: number) =>
    client.request<Person>(`/app/people/${id}`, {
      method: 'PATCH', body: changes, ifMatch: `"${version}"`,
    }),
  remove: (id: number) => client.request<void>(`/app/people/${id}`, { method: 'DELETE' }),
});

export interface UserAvatar {
  id: number; variant_name: string; name: string; status: string; image_url: string | null; voice_id: number | null; version: number; verified: boolean;
  /** Стиль варианта (docs/03-api §7.2): real · illus · 3d; без поля — «Реалистичный» */
  base?: string | null;
  /** Цвета силуэта варианта без картинки (§7.2 tint) */
  tint?: { from: string; to: string } | null;
  /** Когда вариант создан (§7.2): строка «Создан 1 октября» в «Каким вы будете» */
  created_at?: string | null;
}
